import { useEffect, useRef } from 'react';
import { useAuthStore, useRegisterPushToken } from '@sportschool/shared-logic';

/**
 * The native shell injects its platform; app versions released before that are recognized by the WebView user agent.
 */
function detectMobilePlatform(): string {
  const injected = (window as unknown as { mobileDevicePlatform?: string }).mobileDevicePlatform;
  if (injected) return injected;
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) return 'ios';
  return 'expo';
}

/**
 * Automatically listens for mobile push token and registers it with the backend API
 * when running inside the mobile app container and user is authenticated.
 * Skipped while impersonating, so the admin's device is never bound to the impersonated user.
 */
export function useMobilePushTokenSync(isAuthenticated: boolean, schoolId?: string) {
  const registerMutation = useRegisterPushToken(schoolId);
  const isImpersonating = useAuthStore((s) => !!s.impersonationToken);
  const lastRegisteredTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || isImpersonating || typeof window === 'undefined') return;

    const tryRegister = (token: string) => {
      if (!token) return;
      // Remember the device token so logout can unregister it.
      (window as unknown as { __EXPO_PUSH_TOKEN__?: string }).__EXPO_PUSH_TOKEN__ = token;
      if (lastRegisteredTokenRef.current === token) return;
      lastRegisteredTokenRef.current = token;
      registerMutation.mutate({
        token,
        platform: detectMobilePlatform(),
      });
    };

    // 1. Check if token already injected on window
    const existingToken = (window as unknown as { __EXPO_PUSH_TOKEN__?: string }).__EXPO_PUSH_TOKEN__;
    if (existingToken) {
      tryRegister(existingToken);
    }

    // 2. Listen for custom event when token is resolved dynamically
    const handlePushTokenEvent = (e: Event) => {
      const customEvent = e as CustomEvent<string>;
      if (customEvent.detail) {
        tryRegister(customEvent.detail);
      }
    };

    window.addEventListener('expoPushToken', handlePushTokenEvent);

    // 3. Signal to React Native container that we are ready to receive push token
    if ((window as unknown as { ReactNativeWebView?: { postMessage: (msg: string) => void } }).ReactNativeWebView) {
      (window as unknown as { ReactNativeWebView: { postMessage: (msg: string) => void } }).ReactNativeWebView.postMessage(
        JSON.stringify({ type: 'GET_PUSH_TOKEN' })
      );
    }

    return () => {
      window.removeEventListener('expoPushToken', handlePushTokenEvent);
    };
  }, [isAuthenticated, isImpersonating, registerMutation, schoolId]);
}
